import React, { useState } from "react";
import PreviewFrame from "./PreviewFrame";
import DeviceFrame from "./DeviceFrame";
import PlatformChrome from "./PlatformChrome";
import AssetSlot from "./AssetSlot";
import CaptionBlock from "./CaptionBlock";
import ChromeOverlays from "./ChromeOverlays";

/**
 * VellaChatMessage
 * ----------------
 * Wraps a single Vella chat turn that includes a platform-accurate preview.
 * Composes the 7-component subtree from Vella_Preview_Mockup_Spec.md sec. 8.1.
 *
 * Below the preview, three action buttons let the user iterate:
 *   - Another angle  → re-runs with a different camera/composition
 *   - Different look → re-runs with a different style/treatment
 *   - Looks good     → confirms and moves on
 *
 * Props:
 *   - asset, caption, hashtags, brand, platform, postType, variant, leadText
 *   - onAnotherAngle?:   () => void  — handler for the "Another angle" button
 *   - onDifferentLook?:  () => void  — handler for the "Different look" button
 *   - onLooksGood?:      () => void  — handler for the "Looks good" button
 *   - actions?: React node — fully custom action bar (overrides the defaults)
 */
export default function VellaChatMessage({
  asset,
  caption,
  hashtags,
  brand,
  platform = "Instagram",
  postType = "Feed",
  variant,
  leadText,
  onAnotherAngle,
  onDifferentLook,
  onLooksGood,
  onUpscale,
  actions,
}) {
  // Render the default action button row when callbacks are provided AND no
  // explicit `actions` override was passed in.
  const showDefaultActions =
    !actions && (onAnotherAngle || onDifferentLook || onLooksGood || onUpscale);

  return (
    <div
      className="vella-chat-message"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 12,
        marginTop: 12,
      }}
    >
      {leadText ? (
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: "#0F172A",
            lineHeight: 1.5,
          }}
        >
          {leadText}
        </p>
      ) : null}

      <PreviewFrame variant={variant}>
        <DeviceFrame type="iphone-15-pro">
          <PlatformChrome
            platform={platform}
            postType={postType}
            brand={brand}
            asset={asset}
            caption={caption}
            hashtags={hashtags}
          >
            <AssetSlot asset={asset} />
            <CaptionBlock text={caption} hashtags={hashtags} brand={brand} />
            <ChromeOverlays platform={platform} postType={postType} />
          </PlatformChrome>
        </DeviceFrame>
      </PreviewFrame>

      {actions ? (
        <div className="vella-chat-message-actions">{actions}</div>
      ) : showDefaultActions ? (
        <PreviewActionButtons
          onAnotherAngle={onAnotherAngle}
          onDifferentLook={onDifferentLook}
          onLooksGood={onLooksGood}
          onUpscale={onUpscale}
          showUpscale={asset?.type === "video"}
        />
      ) : null}
    </div>
  );
}

/**
 * PreviewActionButtons
 * --------------------
 * The three default actions surfaced under every preview. Disabling a
 * button after a single click prevents accidental double-fire (each request
 * spends generation credits server-side).
 */
function PreviewActionButtons({ onAnotherAngle, onDifferentLook, onLooksGood, onUpscale, showUpscale }) {
  const [pressed, setPressed] = useState(null);

  const buttons = [
    { id: "angle", label: "Another angle", handler: onAnotherAngle },
    { id: "look", label: "Different look", handler: onDifferentLook },
    ...(showUpscale && onUpscale ? [{ id: "upscale", label: "Upscale to 4K", handler: onUpscale }] : []),
    { id: "good", label: "Looks good", handler: onLooksGood, primary: true },
  ];

  const handle = (b) => () => {
    if (pressed) return;
    setPressed(b.id);
    if (b.handler) b.handler();
  };

  return (
    <div
      className="vella-chat-message-actions"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      {buttons.map((b) => {
        const isPrimary = !!b.primary;
        const isPressed = pressed === b.id;
        const disabled = !!pressed;
        return (
          <button
            key={b.id}
            type="button"
            onClick={handle(b)}
            disabled={disabled}
            data-action={b.id}
            style={{
              border: "1px solid",
              borderColor: isPrimary
                ? "rgba(14,165,233,0.55)"
                : "rgba(15,23,42,0.18)",
              background: isPressed
                ? "rgba(14,165,233,0.18)"
                : isPrimary
                ? "linear-gradient(135deg, rgba(14,165,233,0.16) 0%, rgba(14,165,233,0.10) 100%)"
                : "rgba(255,255,255,0.65)",
              backdropFilter: "blur(20px) saturate(160%)",
              WebkitBackdropFilter: "blur(20px) saturate(160%)",
              padding: "8px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              color: isPrimary ? "#0369A1" : "#0F172A",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled && !isPressed ? 0.5 : 1,
              transition: "background 160ms ease, opacity 160ms ease",
            }}
          >
            {b.label}
          </button>
        );
      })}
    </div>
  );
}
